/* ==========================================================================
   THROTTLE CO — product.js (PDP behaviors)
   ==========================================================================

   Loaded by sections/main-product.liquid (defer). Vanilla, no deps.

   <tc-variant-picker data-section data-url>
     - Reads product JSON from the section root: [data-product-json]
       ({{ product | json }} — same shape as the /products/handle.js endpoint).
     - Option inputs: [data-option-input][data-option-position] (radio pills
       or selects). On change: finds the matching variant and updates
         • hidden input[name="id"][data-variant-id] in the product form
         • [data-price-wrapper] (sale/compare markup, tcFormatMoney)
         • [data-atc-button] + [data-atc-text] (Add / Sold out / Unavailable)
         • URL ?variant= (history.replaceState)
         • featured media ([data-media-item][data-media-id])
         • sticky bar price/button ([data-sticky-*])
     - Dispatches 'variant:changed' on the section root (detail: {variant}).

   Delegated hooks (work without a variant picker too):
     [data-thumb][data-media-id]      gallery thumbnail switching
     [data-sticky-atc-button][data-form]  submits the main product form
     [data-share-button]              navigator.share, clipboard fallback

   Sticky ATC bar: IntersectionObserver on the main [data-atc-button] toggles
   .is-visible on [data-sticky-atc] once the buy box scrolls out of view.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Media helpers (shared by variant picker + thumbnail clicks)
     ------------------------------------------------------------------------ */
  function activateMedia(root, mediaId) {
    if (!root || !mediaId) return;
    const items = root.querySelectorAll('[data-media-item]');
    let found = false;
    items.forEach((item) => {
      const active = item.getAttribute('data-media-id') === String(mediaId);
      item.classList.toggle('is-active', active);
      if (active) {
        found = true;
        // Pause any playing videos in the media we just left.
        root.querySelectorAll('[data-media-item]:not(.is-active) video').forEach((v) => v.pause());
      }
    });
    if (!found) return;
    root.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.classList.toggle(
        'is-active',
        thumb.getAttribute('data-media-id') === String(mediaId)
      );
    });
  }

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-thumb]');
    if (!thumb) return;
    const root = thumb.closest('[data-product-root]') || document;
    activateMedia(root, thumb.getAttribute('data-media-id'));
  });

  /* ------------------------------------------------------------------------
     <tc-variant-picker>
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]');
      if (!this.root) return;

      const jsonEl = this.root.querySelector('[data-product-json]');
      if (!jsonEl) return;
      try {
        this.product = JSON.parse(jsonEl.textContent);
      } catch (error) {
        console.warn('[tc-variant-picker] bad product JSON', error);
        return;
      }

      this.strings = {
        add: this.getAttribute('data-text-add') || 'Add to cart',
        soldOut: this.getAttribute('data-text-soldout') || 'Sold out',
        unavailable: this.getAttribute('data-text-unavailable') || 'Unavailable',
      };

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.onOptionChange();
      });

      // Sync UI with the initially selected variant (?variant= deep links).
      this.onOptionChange(true);
    }

    /** Current selection, ordered by option position (1-based). */
    selectedOptions() {
      const options = [];
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        const index = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        if (input.tagName === 'SELECT') {
          options[index] = input.value;
        } else if (input.checked) {
          options[index] = input.value;
        }
      });
      return options;
    }

    findVariant(options) {
      return (this.product.variants || []).find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    onOptionChange(initial) {
      const variant = this.findVariant(this.selectedOptions());
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateButtons(variant);
      this.updateMedia(variant);
      if (!initial) this.updateUrl(variant);

      this.root.dispatchEvent(
        new CustomEvent('variant:changed', { detail: { variant: variant || null }, bubbles: true })
      );
    }

    updateForm(variant) {
      this.root.querySelectorAll('[data-variant-id]').forEach((input) => {
        if (variant) input.value = variant.id;
        input.toggleAttribute('disabled', !variant);
      });
    }

    updatePrice(variant) {
      const wrapper = this.root.querySelector('[data-price-wrapper]');
      if (!wrapper || !variant) return;
      const money = window.tcFormatMoney || ((cents) => `$${(cents / 100).toFixed(2)}`);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      let html = '<div class="tc-price tc-price--large' + (onSale ? ' tc-price--on-sale' : '') + '">';
      if (onSale) {
        html +=
          '<span class="tc-price__sale">' + money(variant.price) + '</span>' +
          '<s class="tc-price__compare">' + money(variant.compare_at_price) + '</s>';
      } else {
        html += '<span class="tc-price__regular">' + money(variant.price) + '</span>';
      }
      html += '</div>';
      wrapper.innerHTML = html;

      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
    }

    updateButtons(variant) {
      const label = !variant
        ? this.strings.unavailable
        : variant.available
          ? this.strings.add
          : this.strings.soldOut;
      const enabled = Boolean(variant && variant.available);

      const button = this.root.querySelector('[data-atc-button]');
      if (button) {
        button.toggleAttribute('disabled', !enabled);
        const text = button.querySelector('[data-atc-text]');
        if (text) text.textContent = label;
      }

      const stickyButton = document.querySelector('[data-sticky-atc-button]');
      if (stickyButton) {
        stickyButton.toggleAttribute('disabled', !enabled);
        const stickyText = stickyButton.querySelector('[data-sticky-atc-text]');
        if (stickyText) stickyText.textContent = label;
      }
    }

    updateMedia(variant) {
      if (variant && variant.featured_media && variant.featured_media.id) {
        activateMedia(this.root, variant.featured_media.id);
      }
    }

    updateUrl(variant) {
      if (!variant || !this.dataset.url) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }
  }

  if (!customElements.get('tc-variant-picker')) {
    customElements.define('tc-variant-picker', VariantPicker);
  }

  /* ------------------------------------------------------------------------
     Sticky add-to-cart bar
     ------------------------------------------------------------------------ */
  function initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const mainButton = document.querySelector('[data-product-root] [data-atc-button]');
    if (!bar || !mainButton || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show the bar once the real buy button has scrolled above the fold.
          const below = entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', !entry.isIntersecting && below);
        });
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }

  document.addEventListener('click', (event) => {
    const stickyButton = event.target.closest('[data-sticky-atc-button]');
    if (!stickyButton) return;
    const form = document.getElementById(stickyButton.getAttribute('data-form'));
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  });

  /* ------------------------------------------------------------------------
     Share button — native share sheet, clipboard fallback
     ------------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-button]');
    if (!button) return;

    const url = button.getAttribute('data-share-url') || window.location.href;
    const title = button.getAttribute('data-share-title') || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      const text = button.querySelector('[data-share-text]');
      if (text) {
        const original = text.textContent;
        text.textContent = button.getAttribute('data-copied-text') || 'Link copied';
        setTimeout(() => {
          text.textContent = original;
        }, 2000);
      }
    } catch (error) {
      window.prompt('Copy this link:', url);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtc);
  } else {
    initStickyAtc();
  }
})();
